import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, expand, map, Observable, reduce } from 'rxjs';
import { environment } from '../../environments/environment';
import { Page } from '../models/page.model';
import {
  CreateTestRunCommentRequest,
  TestRunComment,
  TestRunCommentAttachment,
  UpdateTestRunCommentRequest,
} from '../models/test-run-comment.model';

/** Obergrenze des Backends (spring.data.web.pageable.max-page-size); mehr schneidet es still ab. */
const MAX_PAGE_SIZE = 100;

/**
 * Die Kommentar-Historie eines Testlaufs (NEX-1115).
 *
 * Der Pfad haengt am Lauf und nicht am Projekt: Das Backend loest das Projekt aus
 * dem Lauf auf und prueft die Berechtigung dort ({@code TestRunProjectAccess}).
 */
@Injectable({ providedIn: 'root' })
export class TestRunCommentService {
  private readonly http = inject(HttpClient);

  private comments(runId: string): string {
    return `${environment.apiBaseUrl}/api/v1/test-runs/${runId}/comments`;
  }

  private attachments(runId: string, commentId: string): string {
    return `${this.comments(runId)}/${commentId}/attachments`;
  }

  /**
   * Die Historie als Liste — unabhaengig davon, ob das Backend sie als Array oder
   * als Seite liefert (NEX-1183).
   *
   * Die Weiche steht hier, damit die beiden Seiten in beliebiger Reihenfolge
   * ausgeliefert werden koennen: Ohne sie bekaeme das Panel nach dem BE-Merge ein
   * Objekt statt eines Arrays und zeichnete gar nichts mehr. Sie entfaellt mit
   * NEX-1184, sobald das Panel selbst seitenweise nachlaedt.
   */
  list(runId: string): Observable<TestRunComment[]> {
    return this.http
      .get<TestRunComment[] | Page<TestRunComment>>(this.comments(runId))
      .pipe(map((response) => (Array.isArray(response) ? response : response.content)));
  }

  /**
   * Die ganze Historie ueber alle Seiten, fuer Ansichten ohne eigenes Nachladen.
   * Scheitert eine Seite, scheitert der ganze Aufruf: Die juengsten Eintraege stehen
   * zuletzt, eine Teilliste faellt also nicht auf.
   */
  listAll(runId: string): Observable<TestRunComment[]> {
    const page = (number: number): Observable<Page<TestRunComment>> =>
      this.http.get<Page<TestRunComment>>(this.comments(runId), { params: { page: number, size: MAX_PAGE_SIZE } });
    return page(0).pipe(
      expand((response, index) => (response.last || response.content.length === 0 ? EMPTY : page(index + 1))),
      reduce<Page<TestRunComment>, TestRunComment[]>((all, response) => [...all, ...response.content], []),
    );
  }

  create(runId: string, request: CreateTestRunCommentRequest): Observable<TestRunComment> {
    return this.http.post<TestRunComment>(this.comments(runId), request);
  }

  update(runId: string, commentId: string, request: UpdateTestRunCommentRequest): Observable<TestRunComment> {
    return this.http.patch<TestRunComment>(`${this.comments(runId)}/${commentId}`, request);
  }

  delete(runId: string, commentId: string): Observable<void> {
    return this.http.delete<void>(`${this.comments(runId)}/${commentId}`);
  }

  uploadAttachment(runId: string, commentId: string, file: File): Observable<TestRunCommentAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<TestRunCommentAttachment>(this.attachments(runId, commentId), formData);
  }

  downloadAttachment(runId: string, commentId: string, attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.attachments(runId, commentId)}/${attachmentId}`, { responseType: 'blob' });
  }

  deleteAttachment(runId: string, commentId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.attachments(runId, commentId)}/${attachmentId}`);
  }
}
